// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Col,
  Row,
} from 'reactstrap';
import {
  Loader,
} from '@vroskus/library-components';

// Helpers
import {
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultGetData,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import RegattaStatusSection from '../../sections/RegattaCommonSections/RegattaStatusSection';
import RegattaSettingsSection from '../../sections/RegattaCommonSections/RegattaSettingsSection';
import RegattaRegistrationSection from '../../sections/RegattaCommonSections/RegattaRegistrationSection';
import RegattaGroupsSection from '../../sections/RegattaCommonSections/RegattaGroupsSection';
import RegattaRequirementsSection from '../../sections/RegattaCommonSections/RegattaRequirementsSection';
import RegattaChecksSection from '../../sections/RegattaCommonSections/RegattaChecksSection';
import RegattaAdminsSection from '../../sections/RegattaCommonSections/RegattaAdminsSection';
import RegattaContactsSection from '../../sections/RegattaCommonSections/RegattaContactsSection';
import RegattaDescriptionSection from '../../sections/RegattaCommonSections/RegattaDescriptionSection';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  item: $FullAdminRegatta | null;
  loading: boolean;
};

const pageId = adminPages.regattaSettingsPage;

class RegattaSettingsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      item: null,
      loading: false,
    };
  }

  componentDidMount(): void {
    const {
      main,
      redirect,
    } = this.props;

    if (main.activeRegattaId === null) {
      redirect({
        pathname: '/regattas',
      });
    }

    this.getDataWithLoading();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegionSeasonId !== null && isDiff(
      main.activeRegionSeasonId,
      prevProps.main.activeRegionSeasonId,
    )) {
      this.getDataWithLoading();
    }

    if (main.activeRegattaId !== null && isDiff(
      main.activeRegattaId,
      prevProps.main.activeRegattaId,
    )) {
      this.getDataWithLoading();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegattaId === data.RegattaId && data.modelName === 'Regatta') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getDataWithLoading(): Promise<void> {
    await this.setStateAsync({
      loading: true,
    });

    await this.getData();
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionSeasonId = main.activeRegionSeasonId;
    const RegattaId = main.activeRegattaId;

    await defaultGetData({
      action: async () => {
        if (RegionSeasonId !== null) {
          let item: $FullAdminRegatta | null = null;

          if (RegattaId !== null) {
            item = await Services.Api.getOneRegatta({
              id: RegattaId,
            });
          }

          await this.setStateAsync({
            item,
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderContent() {
    const {
      item,
    } = this.state;

    return (item !== null ? (
      <>
        <RegattaStatusSection
          id={adminSections[pageId].status}
          item={item}
        />
        <RegattaSettingsSection
          id={adminSections[pageId].settings}
          item={item}
        />
        <RegattaDescriptionSection
          id={adminSections[pageId].description}
          item={item}
        />
        <RegattaGroupsSection
          id={adminSections[pageId].groups}
          item={item}
        />
        <RegattaRegistrationSection
          id={adminSections[pageId].registration}
          item={item}
        />
        <Row>
          <Col md={6}>
            <RegattaRequirementsSection
              id={adminSections[pageId].requirements}
              item={item}
            />
          </Col>
          <Col md={6}>
            <RegattaChecksSection
              id={adminSections[pageId].checks}
              item={item}
            />
          </Col>
        </Row>
        <RegattaContactsSection
          id={adminSections[pageId].contacts}
          item={item}
        />
        <RegattaAdminsSection
          id={adminSections[pageId].admins}
          item={item}
        />
      </>
    ) : null);
  }

  render() {
    const {
      loading,
    } = this.state;

    return (
      <Page id={pageId}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderContent()}
        </Loader>
      </Page>
    );
  }
}

export default RegattaSettingsPageView;
